/**
 * Actycrea: map.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { deepMerge } from '../helpers/helpers.js';
import { onActive } from '../helpers/events.js';
import { getCurrentBreakpoint } from '../helpers/breakpoints.js';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

/* =========================
 * Selectores con PREFIX + fallback legado
 * =======================*/
const SELECTOR             = `data-${PREFIX}-mygm`;
const SELECTOR_DATA        = `[${SELECTOR}]`;
const SELECTOR_DATA_MAP       = `[data-${PREFIX}-map]`

/* =========================
 * Config
 * =======================*/
const DEFAULTS = {
  options: {
    center: { lat: -34.397, lng: 150.644 } ,
    zoom: 6 ,
    mapTypeControl: false,
    zoomControl: false,
    scrollwheel: false,
    scaleControl: false,
    streetViewControl: false ,
    rotateControl: false ,
    tiltControl: false ,
    fullscreenControl: false ,
    keyboardShortcuts: false ,
    disableDoubleClickZoom: true ,
    disableDefaultUI: true ,
    gestureHandling: 'none' ,
    tilt: 0 ,
    heading: 0 ,
    mapId: window?.wphack?.gmaps?.mapid ,
  },
  merge: { },
  bounds: false ,
  zoomMax: 6 ,              // límite máximo efectivo
  items: null,             // array de marcadores [{ location:{lat,lng}, title:{title}, size, data, region }]
  itemsGlobalKey: 'ITEMS'  // si no hay items, intenta window[itemsGlobalKey]
};

const MyColors = {
  white:      '--bs-white',
  black:      '--bs-black',
  primary:    '--bs-primary',
  secondary:  '--bs-secondary',
  warning:    '--bs-warning',
  dark:       '--bs-dark',
  medium:     '--bs-medium',
  light:      '--bs-light'
};

/* =========================
 * Loader (compat con wphack)
 * =======================*/
const MyLoader = {
  key:        window?.wphack?.gmaps?.front,
  v:          'weekly',
  language:   window?.wphack?.locale || 'en',
  libraries:  [ 'marker' ]
};

/* =========================
 * Clase
 * =======================*/
export class MyGM {
  /**
   * @param {HTMLElement} element contenedor raíz
   * @param {Object} config opciones user
   */
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error('MyGM: element inválido');

    this._config    = deepMerge( DEFAULTS , config );
    this._element   = element;

    this._mapEl     = element.querySelector( SELECTOR_DATA_MAP );
    if( !this._mapEl ){ return; }

    this._mapEl.id = `MAP${PREFIX.toUpperCase()}${Math.floor( ( Math.random() * 1e6 ) + 1 )}`;

    // estado
    this._google      = null;
    this._map         = null;
    this._infoWindow  = null;
    this._markers     = [ ];
    this._bounds      = [ ];
    this._active      = -1;
    this._colors      = { };
    this._theme       = [ ];
    this._zoomMax     = Number( this._config.zoomMax ) || 5;

    // colores + tema
    Object.entries( MyColors ).forEach( ([k,v]) => this._colors[k] = getComputedStyle( document.documentElement ).getPropertyValue( v ).trim() );

    this._options = deepMerge( this._config.options , this._config.merge );

    this._onLoad( ).catch( ( err ) => this._onError( err ) );
  }

  _initBounds( ){
    if( this._config.bounds === true ) this._bounds = new this._LatLngBounds( );
  }
  _initInfoWindow( ){
    this._infoWindow = new this._InfoWindow( );
    this._infoWindow.setOptions({ pixelOffset: new this._Size(0, -8) });
   }
  _fitBounds( ){
    if( this._config.bounds === true && ! this._bounds.isEmpty( ) )
      this._map.fitBounds( this._bounds );
  }

  async _onLoad( ){

    if ( ! MyLoader.key ) { console.error('MyGM: key no definida'); return; }

    setOptions( MyLoader );

    const { Map , InfoWindow }      = await importLibrary( 'maps' );
    const { LatLng , LatLngBounds , Size } = await importLibrary( 'core' );
    const { AdvancedMarkerElement } = await importLibrary( 'marker' );

    this._Map = Map;
    this._InfoWindow = InfoWindow;
    this._LatLng = LatLng;
    this._LatLngBounds = LatLngBounds;
    this._AdvancedMarkerElement = AdvancedMarkerElement;
    this._Size = Size;

    // Crear mapa
    this._map = new this._Map( this._mapEl , this._options );

    this._initBounds( );
    this._initInfoWindow( );

    const items = Array.isArray( this._config.items )
      ? this._config.items
      : ( window?.[ this._config.itemsGlobalKey ] && Array.isArray( window[ this._config.itemsGlobalKey ] ) ? window[ this._config.itemsGlobalKey ] : [] );

    items.forEach( ( it , idx ) => this._addMarker( it , idx ) );

    if( this._markers.length ){
      this._fitBounds( );
    }

    window.addEventListener( onActive , ( ev ) => this._activateMarker( ev ) );
    window.addEventListener( 'breakpointChange' , ( ev ) => this._changeBP( ev ) );

  }

  _onError( error ){
    console.error( 'MyGM: loader error' , error );
  }

  _buildTheme( ){
    const t = [];
    MyThemeBase.forEach( v => {
      const item = { ...v , stylers: [ ...(v.stylers || []) ] };
      if( v.featureType === 'water' || v.featureType === 'poi.park' || v.featureType === 'poi' ){
        item.stylers.push( { color: this._colors.light } );
      } else if( v.featureType === 'landscape' || v.featureType === 'transit' ){
        item.stylers.push( { color: this._colors.white } );
      } else if( v.featureType === 'road.highway' ){
        item.stylers.push( { color: this._colors.white } );
      } else if( v.featureType === 'road.arterial' || v.featureType === 'road.local' ){
        item.stylers.push( { color: this._colors.white } );
      } else if( v.elementType === 'labels.text.stroke' ){
        item.stylers.push( { color: this._colors.white } );
      } else if( v.elementType === 'labels.text.fill' ){
        item.stylers.push( { color: this._colors.white } );
      } else if( v.featureType === 'administrative' ){
        item.stylers.push( { color: this._colors.white } );
      }
      t.push( item );
    } );
    return t;
  }

  _addMarker( item , index ){
    const pos       = new this._LatLng( item.location.lat , item.location.lng );
    const content   = this._squareContent( 'medium' , 16 );

    const marker    = new this._AdvancedMarkerElement({
      map:            this._map,
      position:       pos,
      title:          item?.title || '',
      content:        content,
      gmpClickable:   true
    });

    marker.__data = { index, image: item?.image || '', title: item?.title || '' };

    if( this._config.bounds ) this._bounds.extend( pos );
    this._markers.push( marker );

  }

  _squareContent( colorKey , size = 16 ){
    const el        = document.createElement( 'div' );
    el.style.width  = `${size}px`;
    el.style.height = `${size}px`;
    el.style.background = this._colors[ colorKey ] || this._colors.medium;
    el.style.boxSizing = 'border-box';
    return el;
  }

  _setMarkerState( marker , active ){
    if( marker?.content ){
      marker.content.style.background = active ? this._colors.primary : this._colors.medium;
      marker.zIndex = active ? 100 : 1;
    }
  }

  _activateMarker( ev ){
    const marker = this._markers[ ev.detail?.index ];
    const idx = marker.__data.index;

    if( this._active === idx ){
      // toggling: cerrar y desactivar
      this._infoWindow.close();
      this._setMarkerState( marker , false );
      this._active = -1;
      return;
    }

    // desactivar anterior
    if( this._active >= 0 ){
      const prev = this._markers[ this._active ];
      this._setMarkerState( prev , false );
    }

    // activar actual
    this._setMarkerState( marker , true );
    this._active = idx;

    // animación visual del pin
    // this._pop( marker );

    // centrar con desplazamiento suave
    if(this._map.panTo) this._map.panTo( marker.position );

    // abrir InfoWindow con solo la imagen
    const url = marker.__data.image || '';
    const html = url ? `<img src='${url}' />` : '';
    this._infoWindow.setContent( html );
    this._changeBP( ev );
    this._infoWindow.open( { anchor: marker, map: this._map , shouldFocus: false } );
  }

  _changeBP( ev ){
    this._infoWindow.setOptions({ pixelOffset: new this._Size( [ 'xs' , 'sm' ].includes( getCurrentBreakpoint() ) ? 42 : 62 , 0 ) });
  }

  /* ============
   * API pública mínima
   * ===========*/

  getMap( ){ return this._map; }
  getMarkers( ){ return this._markers.slice(); }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [];
    nodes.forEach( ( el ) => {
      if( el._mygmInstance ) return;
      let cfg = { };
      const raw = el.getAttribute( SELECTOR );
      if( raw ){
        try { cfg = JSON.parse( raw ); } catch( _ ){ cfg = { }; }
      }
      el._mygmInstance = new MyGM( el , cfg );
      instances.push( el._mygmInstance );
    } );
    return instances;
  }
}

export default MyGM;
